import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "./AuthContext";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeEntity, setActiveEntity] = useState(null);
  const [tenant, setTenant] = useState(null);

  const loadEntities = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/entities");
      setEntities(data);
      // Don't auto-select entity - show consolidated by default
      if (data.length > 0 && !activeEntity) {
        setActiveEntity(null); // null = consolidated view
      }
    } catch (e) {
      console.error("Failed to load entities", e);
    }
  }, [user]);

  const loadCategories = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch (e) {
      console.error("Failed to load categories", e);
    }
  }, [user]);

  const loadTenant = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/tenants/current");
      setTenant(data);
    } catch (e) {
      console.error("Failed to load tenant", e);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadEntities();
      loadCategories();
      loadTenant();
    }
  }, [user, loadEntities, loadCategories, loadTenant]);

  const switchEntity = (entity) => {
    setActiveEntity(entity);
  };

  const getCategoryName = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    return cat ? cat.name : catId;
  };

  const getEntityName = (entityId) => {
    const ent = entities.find((e) => e.id === entityId);
    return ent ? ent.name : entityId;
  };

  return (
    <AppContext.Provider value={{
      entities, categories, activeEntity, tenant,
      switchEntity, getCategoryName, getEntityName,
      loadEntities, loadCategories, loadTenant,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be inside AppProvider");
  return ctx;
}
